import { Cause, Context, Effect, Exit, Layer, Queue, Ref, Schema, Scope } from "effect";
import { resourceInvalid } from "@workerstation/shared/errors/resource";

export const WORKFLOWS_CHANGED_EVENT = "workerstation:workflows-changed";
export type WorkflowChange = { readonly _tag: "All" } | { readonly _tag: "Workspace"; readonly workspaceId: string };
export type WorkflowChangeListener = (event: Event) => void;
export class WorkflowChangeIo extends Context.Service<WorkflowChangeIo, {
  readonly add: (listener: WorkflowChangeListener) => Effect.Effect<void>;
  readonly remove: (listener: WorkflowChangeListener) => Effect.Effect<void>;
  readonly publish: (workspaceId: string) => Effect.Effect<void>;
}>()("workerstation/frontend/WorkflowChangeIo") {}
export class WorkflowChanges extends Context.Service<WorkflowChanges, {
  readonly subscribe: (listener: (change: WorkflowChange) => Effect.Effect<void>) => Effect.Effect<void, never, Scope.Scope>;
  readonly publish: (workspaceId: string) => Effect.Effect<void>;
}>()("workerstation/frontend/WorkflowChanges") {}
const Detail = Schema.Struct({ workspaceId: Schema.optionalKey(Schema.String) });
const decode = Effect.fn("Frontend.WorkflowChanges.decode")(function* (event: Event) {
  const detail = yield* Effect.try({ try: () => (event as CustomEvent<unknown>).detail, catch: () => resourceInvalid("Invalid Workflow notification.") });
  const value = yield* Schema.decodeUnknownEffect(Detail)(detail ?? {}).pipe(Effect.mapError(() => resourceInvalid("Invalid Workflow notification.")));
  return value.workspaceId ? { _tag: "Workspace" as const, workspaceId: value.workspaceId } : { _tag: "All" as const };
});

export const WorkflowChangesLive = (owner: Scope.Scope) => Layer.effect(WorkflowChanges, Effect.gen(function* () {
  const io = yield* WorkflowChangeIo, scope = yield* Scope.Scope, queue = yield* Queue.unbounded<Event>();
  const subscribers = yield* Ref.make<ReadonlyMap<object, (change: WorkflowChange) => Effect.Effect<void>>>(new Map());
  const failures = yield* Ref.make<readonly Cause.Reason<never>[]>([]), closed = yield* Ref.make(false);
  const admissionClosed = () => owner.state._tag === "Closed" || scope.state._tag === "Closed" || Ref.getUnsafe(closed);
  yield* Effect.addFinalizer(() => Effect.gen(function* () {
    const reasons = yield* Ref.get(failures);
    if (reasons.length) return yield* Effect.failCause(Cause.fromReasons(reasons));
  }));
  yield* Scope.addFinalizer(scope, Queue.shutdown(queue));
  const receive: WorkflowChangeListener = event => { if (!admissionClosed()) Queue.offerUnsafe(queue, event); };
  yield* Effect.addFinalizer(() => Ref.set(closed, true).pipe(Effect.andThen(Ref.set(subscribers, new Map())), Effect.andThen(io.remove(receive))));
  yield* io.add(receive);
  yield* Effect.forkIn(Effect.forever(Effect.gen(function* () {
    if (admissionClosed()) return yield* Effect.interrupt;
    const event = yield* Queue.take(queue);
    if (admissionClosed()) return yield* Effect.interrupt;
    const parsed = yield* Effect.exit(decode(event));
    if (Exit.isFailure(parsed)) return;
    const listeners = [...(yield* Ref.get(subscribers))];
    for (const [identity, listen] of listeners) {
      if (admissionClosed() || !(yield* Ref.get(subscribers)).has(identity)) continue;
      const result = yield* Effect.exit(listen(parsed.value));
      if (Exit.isFailure(result)) yield* Ref.update(failures, current => [...current, ...result.cause.reasons.filter(reason => reason._tag !== "Interrupt")]);
    }
  })), scope);
  return WorkflowChanges.of({
    subscribe: Effect.fn("Frontend.WorkflowChanges.subscribe")(function* (listener) {
      const caller = yield* Scope.Scope;
      const callerClosed = () => caller.state._tag === "Closed";
      if (admissionClosed() || callerClosed()) return yield* Effect.interrupt;
      const identity = {};
      yield* Scope.addFinalizer(caller, Ref.update(subscribers, current => { const next = new Map(current);next.delete(identity);return next; }));
      if (admissionClosed() || callerClosed()) return yield* Effect.interrupt;
      const admitted = yield* Ref.modify(subscribers, current => admissionClosed() || callerClosed() ? [false, current] as const : [true, new Map(current).set(identity, listener)] as const);
      if (!admitted) return yield* Effect.interrupt;
    }, Effect.uninterruptible),
    publish: Effect.fn("Frontend.WorkflowChanges.publish")((workspaceId: string) => Effect.suspend(() => scope.state._tag === "Closed" || Ref.getUnsafe(closed) ? Effect.interrupt : io.publish(workspaceId)))
  });
}));
export const WorkflowChangeIoTest = (io: Context.Service.Shape<typeof WorkflowChangeIo>) => Layer.succeed(WorkflowChangeIo, io);
